/* eslint-disable import/no-extraneous-dependencies */
import { applyMiddleware, combineReducers, createStore, Store } from 'redux';
import { composeWithDevTools } from 'redux-devtools-extension';
import { createLogger } from 'redux-logger';
import createSagaMiddleware from 'redux-saga';
import rollbar from 'lib/ErrorLogger';
import { accountRecoveryReducer } from 'store/AccountRecovery';
import { apiReducer } from 'store/ApiClient';
import { App, appReducer } from 'store/App';
import { calculatorRulesReducer } from 'store/CalculatorRules';
import { achReducer, alertsReducer, employeePayrollTipsReducer, employeesReducer, jobsReducer, ledgerReducer, paymentsReducer, payoutsReducer, payPeriodsReducer, posCategoriesReducer } from 'store/Collections';
import { daySegmentsReducer } from 'store/Collections/DaySegments';
import { employeeReducer } from 'store/Employee';
import { employersReducer } from 'store/Employers';
import { integrationReducer } from 'store/Integration';
import { invitesReducer } from 'store/Invites';
import { paymentReducer } from 'store/Payment/reducer';
import { rootSaga } from 'store/root/sagas';
import { signUpUserReducer } from 'store/SignUp';
import { siteReducer } from 'store/Site';
import { statsReducer } from 'store/Stats';
import { tipAcceptanceReducer } from 'store/TipAcceptance';
import { tipCalculatorReducer } from 'store/TipCalculator';
import { ICollectionState, IStore } from 'store/types';
import { userReducer } from 'store/User';
import { IAction } from 'types';
import { isDevelopment } from 'utils';

declare global {
  interface Window {
    Cypress: any;
    store: any;
  }
}

const allReducers = combineReducers<IStore>({
  api: apiReducer,
  app: appReducer,
  accountRecovery: accountRecoveryReducer,
  collections: combineReducers<ICollectionState>({
    jobs: jobsReducer,
    ach: achReducer,
    alerts: alertsReducer,
    payPeriods: payPeriodsReducer,
    ledger: ledgerReducer,
    daySegments: daySegmentsReducer,
    employeePayrollTips: employeePayrollTipsReducer,
    payments: paymentsReducer,
    payouts: payoutsReducer,
    posCategories: posCategoriesReducer,
    employees: employeesReducer,
  }),
  calculatorRules: calculatorRulesReducer,
  tipAcceptance: tipAcceptanceReducer,
  tipCalculator: tipCalculatorReducer,
  employers: employersReducer,
  employee: employeeReducer,
  payment: paymentReducer,
  stats: statsReducer,
  site: siteReducer,
  integration: integrationReducer,
  invite: invitesReducer,
  signUpUser: signUpUserReducer,
  user: userReducer,
});

const rootReducer = (originalState: IStore, action: IAction) => {
  const stateResetNeeded = action.type === App.LOGOUT_SUCCESS;
  // Reducers are supposed to return the initial state when
  // they are called with undefined as the first argument, no matter the action
  return allReducers(stateResetNeeded ? undefined : originalState, action);
};

export const configureStore = (): Store<IStore> => {
  const composeEnhancers = composeWithDevTools({});
  const sagaMiddleware = createSagaMiddleware({
    onError: (err) => {
      if (!isDevelopment()) {
        rollbar.critical(err);
      }
    },
  });

  const middlewares: any = [];
  middlewares.push(sagaMiddleware);
  if (process.env.NODE_ENV === 'development') middlewares.push(createLogger({ collapsed: () => true }));

  const store = createStore(rootReducer, composeEnhancers(applyMiddleware(...middlewares)));

  // Run the root saga
  sagaMiddleware.run(rootSaga);

  // Expose store when run in Cypress
  if (window.Cypress) window.store = store;

  return store;
};
